import type { Observable } from 'rxjs';

/**
 * What the guard needs from whatever page it is registered on: a page that
 * renders `SkillBuilderHostComponent` and delegates to it (SB-3650 / T5).
 *
 * Typed structurally rather than on `AssistantsPage` because the skill builder
 * is now reachable from two routes. This removes no compile-time check —
 * `Route.canDeactivate` is typed `CanDeactivateFn<any>`, so the concrete
 * signature was never enforced at the registration site.
 */
export interface CanDismissSkillBuilder {
  canDeactivate(): Observable<boolean>;
}

/**
 * CanDeactivate guard for every route that renders the skill builder overlay:
 * `/chats/assistants`, `/chats/teammates/:slug` and `/admin/skills`. Prompts
 * on navigate-away while the builder holds unsaved work.
 *
 * Angular passes `null` as the component when the instance is not available at
 * guard-run time (destroyed / never fully activated during a racing
 * re-navigation) — nothing to deactivate, so allow navigation.
 * (Fixes SQUADBOX-CR: "Cannot read properties of null (reading 'canDeactivate')")
 *
 * NOTE it also runs on a same-route param change where the component is reused
 * — `teammates/:slug` sets `runGuardsAndResolvers: 'paramsOrQueryParamsChange'`,
 * so switching teammates re-runs this against the same page instance. With a
 * clean open builder the allow path closes it, which is intended: the slice
 * lives on the parent route and would otherwise re-open over the new teammate.
 */
export const assistantsCanDeactivateGuard = (
  component: CanDismissSkillBuilder | null,
): Observable<boolean> | boolean =>
  component ? component.canDeactivate() : true;
